import { object, string, array, date,  } from 'yup'
import { transformStringDateFormatToDate } from '@/shared/lib/helpers/transformStringDateFormatToDate.js'
import * as yup from 'yup'

const VictimValidationSchema = object().shape({
	iin: string()
		.nullable()
		.when('isResident', {
			is: true, // выполнить проверку, если isResident === true
			then: (schema) =>
				schema
					.matches(/^\d+$/, 'Недопустимый символ(ы)')
					.min(12, { key: 'min_length', value: 12 })
					.max(12, { key: 'max_length', value: 12 })
					.required({ key: 'required' }),
		})
		.test('custom-isValid-error', function (value) {
			const { isValid, isResident } = this.parent

			if (isResident) {
				if (!isValid) {
					return this.createError({
						message: 'Ошибка в валидации',
						path: this.path,
					})
				}

				return true
			} else {
				return true
			}
		})
		.test('duplicate-iin', 'Дублирующийся ИИН', function (value) {
			if (!value) return true;
			const allInjureds = this.options?.from?.[1]?.value?.injureds;
			if (!Array.isArray(allInjureds)) return true;
			const count = allInjureds.filter(v => v.iin === value).length;
			if (count > 1) {
				return this.createError({
					path: this.path,
					message: 'Дублирующийся ИИН',
				});
			}
			return true;
		}),
	surname: string().nullable(),
	secondname: string().nullable(),
	firstname: string().nullable(),
	birthDate: transformStringDateFormatToDate('dd.MM.yyyy')
		.nullable()
		.when('isResident', {
			is: false,
			then: (schema) => schema.required({ key: 'required' }),
		})
		.max(new Date(), { key: 'date.can_not_be_after_now' }),
	gender: string().required({ key: 'required' }).nullable(),
	passportNumber: string()
		.nullable()
		.when('isResident', {
			is: false, // выполнить проверку, если isResident === false
			then: (schema) => schema.required({ key: 'required' }),
		}),
	issuredBy: string()
		.nullable()
		.when('isResident', {
			is: false, // выполнить проверку, если isResident === false
			then: (schema) => schema.required({ key: 'required' }),
		}),
	issuredWhen: transformStringDateFormatToDate('dd.MM.yyyy')
		.nullable()
		.when('isResident', {
			is: false,
			then: (schema) => schema.required({ key: 'required' }),
		})
		.max(new Date(), { key: 'date.can_not_be_after_now' }),
	positionCode: string().required({ key: 'required' }).nullable(),
	establishedPost: object()
		.shape({
			nameRu: string().nullable(),
			nameKz: string().nullable(),
		})
		.test('nameRuOrNameKz', 'Укажите должность', (value) => {
			return value.nameRu || value.nameKz
		}),
	diagnosis: string().required({ key: 'required' }).nullable(),
})

export const AccidentReportValidationSchema = object().shape({
	location: string().required({ key: 'required' }).nullable(),
	accidentDateTime: transformStringDateFormatToDate('dd.MM.yyyy HH:mm')
		.required({ key: 'required' })
		.nullable()
		.max(new Date(), { key: 'date.can_not_be_after_now' }),
	region: string().required({ key: 'required' }).nullable(),
	district: string().required({ key: 'required' }).nullable(),
	circumstance: string().required({ key: 'required' }).nullable(),
	senderIin: string()
		.matches(/^\d+$/, 'Недопустимый символ(ы)')
		.min(12, { key: 'min_length', value: 12 })
		.max(12, { key: 'max_length', value: 12 })
		.required({ key: 'required' })
		.test('custom-isValid-error', function (value) {
			const { isValid } = this.parent

			if (!isValid) {
				return this.createError({
					message: 'Ошибка в валидации',
					path: this.path,
				})
			}

			return true
		}),
	senderFio: string().required({ key: 'required' }).nullable(),
	senderPhoneNumber: string().required({ key: 'required' }).nullable(),
	senderEmail: string().email('Неверный формат почты!').required({ key: 'required' }).nullable(),
	senderEstablisedPost: object()
		.shape({
			nameRu: string().nullable(),
			nameKz: string().nullable(),
		})
		.test('nameRuOrNameKz', 'Укажите должность', (value) => {
			return value.nameRu || value.nameKz
		}),
	injureds: array().of(VictimValidationSchema),
})

export const AccidentComissionValidationSchema = object().shape({
	accidentId: string().required({ key: 'required' }).nullable(),
	commissions: yup.array().of(
		yup.object().shape({
			bin: string().required({ key: 'required' }).nullable(),
			contactEmail: string().required({ key: 'required' }).nullable(),
			contactNumber: string().required({ key: 'required' }).nullable(),
			establishedPost: object()
				.shape({
					nameRu: string().nullable(),
					nameKz: string().nullable(),
				})
				.test('nameRuOrNameKz', 'Укажите должность', (value) => {
					return value.nameRu || value.nameKz
				}),
			fio: string().required({ key: 'required' }).nullable(),
			fullOrgNameKz: string().required({ key: 'required' }).nullable(),
			fullOrgNameRu: string().required({ key: 'required' }).nullable(),
			iin: string().required({ key: 'required' }).nullable(),
		})
	),
	commissionFileDto: object().shape({
		fileName: string().required({ key: 'required' }).nullable(),
		id: string().required({ key: 'required' }).nullable(),
	}),
})

export const AccidentSeverityValidationSchema = object().shape({
	accidentId: string().required({ key: 'required' }).nullable(),
	bin: string().required({ key: 'required' }).nullable(),
	incidentTypeCode: string().required({ key: 'required' }).nullable(),
	conclusions: yup.array().of(
		yup.object().shape({
			fileDate: string().required({ key: 'required' }).nullable(),
			fileId: string().required({ key: 'required' }).nullable(),
			fileName: string().required({ key: 'required' }).nullable(),
			injuredExternalId: string().required({ key: 'required' }).nullable(),
			mimeType: string().required({ key: 'required' }).nullable(),
			severityDegreeCode: string().required({ key: 'required' }).nullable(),
		})
	),
})

export const FillingActValidationSchema = object().shape({
	accidentDateTime: string().required({ key: 'required' }).nullable(),
	region: string().required({ key: 'required' }).nullable(),
	district: string().required({ key: 'required' }).nullable(),
	location: string().required({ key: 'required' }).nullable(),
	circumstance: string().required({ key: 'required' }).nullable(),
	accidentTypeCode: string().required({ key: 'required' }).nullable(),
	injureds: array().of(
		object().shape({
			actNumber: string().required({ key: 'required' }).nullable(),
			actDate: string().required({ key: 'required' }).nullable(),
			experience: string().required({ key: 'required' }).nullable(),
			employerFault: string().required({ key: 'required' }).nullable(),
			physicalConditionCode: string().required({ key: 'required' }).nullable(),
			gender: string().required({ key: 'required' }).nullable(),
			establishedPost: object()
				.shape({
					nameRu: string().nullable(),
					nameKz: string().nullable(),
				})
				.test('nameRuOrNameKz', 'Укажите должность', (value) => {
					return value.nameRu || value.nameKz
				}),
			positionCode: string().required({ key: 'required' }).nullable(),
		})
	),
	actCausesDtos: array().of(
		object().shape({
			causeCode: string().required({ key: 'required' }).nullable(),
			lawCode: string().required({ key: 'required' }).nullable(),
		})
	),
	actEventDtos: array().of(
		object()
			.shape({
				description: string().required({ key: 'required' }).nullable(),
				endDate: string().required({ key: 'required' }).nullable(),
			}).test(
			'eventIinOrEventEstablishedPost',
			'Укажите ИИН сотрудника или должность',
			function (value) {
				const { eventFio, eventEstablishedPost } = value || {};
				if (eventFio || eventEstablishedPost) {
					return true;
				}
				return this.createError({
					path: `${this.path}.eventFio`,
					message: 'Укажите ИИН сотрудника или должность',
				});
			}
		)
	),
	characteristic: string().required({ key: 'required' }).nullable(),
	physicalConditionCode: string().nullable(),
	icd: string().nullable(),
	eyewitnessesDtos: array().of(
		object().shape({
			witnessIin: string()
				.nullable()
				.test(
					'length',
					{ key: 'required' },
					(value) => !value || value.length === 12,
				),
			witnessFio: string()
				.nullable()
				.when('witnessIin', {
					is: (value) => !!value,
					then: (schema) => schema.required({ key: 'required' }).nullable(),
					otherwise: (schema) => schema.nullable(),
				})
		})
	).nullable(),
})
